import { TERMINAL_RUN_STATUSES, type RunStatus } from '@1qa/shared';
import type { Run } from './types';

export function formatDuration(ms: number | null | undefined): string {
  if (!ms || ms < 0) return '—';
  if (ms < 1000) return `${ms} ms`;
  const s = ms / 1000;
  if (s < 60) return `${s.toFixed(s < 10 ? 1 : 0)} s`;
  const m = Math.floor(s / 60);
  return `${m}m ${Math.round(s % 60)}s`;
}

const rtf = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' });

/** 12.4 MB, 980 KB. */
export function formatBytes(n: number): string {
  if (n >= 1024 * 1024 * 1024) return `${(n / 1024 ** 3).toFixed(1)} GB`;
  if (n >= 1024 * 1024) return `${(n / 1024 ** 2).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(n / 1024))} KB`;
}

export function timeAgo(iso: string | null | undefined): string {
  if (!iso) return '—';
  const diff = (new Date(iso).getTime() - Date.now()) / 1000;
  const abs = Math.abs(diff);
  if (abs < 45) return 'just now';
  if (abs < 3600) return rtf.format(Math.round(diff / 60), 'minute');
  if (abs < 86400) return rtf.format(Math.round(diff / 3600), 'hour');
  if (abs < 86400 * 30) return rtf.format(Math.round(diff / 86400), 'day');
  return new Date(iso).toLocaleDateString();
}

export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

export function isActive(status: RunStatus): boolean {
  return !TERMINAL_RUN_STATUSES.includes(status);
}

/**
 * Whether the user may delete this run's report now. Mirrors the API: the runner must be done with it
 * (a run cancelled while running keeps finishedAt empty until it actually stops).
 */
export function isRunDeletable(run: Run, canDelete?: (run: Run) => boolean): boolean {
  if (isActive(run.status) || (run.status === 'CANCELLED' && !run.finishedAt)) return false;
  return Boolean(canDelete?.(run));
}

export function runDuration(run: Pick<Run, 'summary' | 'startedAt' | 'finishedAt'>): number | undefined {
  if (run.summary?.durationMs) return run.summary.durationMs;
  if (run.startedAt && run.finishedAt) return new Date(run.finishedAt).getTime() - new Date(run.startedAt).getTime();
  return undefined;
}

export function runCounts(run: Run) {
  const s = run.summary ?? {};
  const results = run.results ?? [];
  const passed = s.passed ?? results.filter((r) => r.status === 'PASSED').length;
  const failed = s.failed ?? results.filter((r) => r.status === 'FAILED').length;
  const errored = s.errored ?? results.filter((r) => r.status === 'ERROR').length;
  const total = s.total ?? (results.length || passed + failed + errored);
  const skipped = Math.max(0, total - passed - failed - errored);
  return { total, passed, failed, errored, skipped };
}

export function projectNameOf(run: Run): string {
  return run.project?.name ?? run.projectName ?? 'Project';
}

/** Short, human-friendly run id: the last 6 characters, e.g. "#k3x9q2". */
export function runShortId(run: Pick<Run, 'id'>): string {
  return `#${run.id.slice(-6)}`;
}

/**
 * Names a run by its tests: "Login", "Login, Checkout" or "Login + 4 more". Falls back to the result
 * names (public shared reports) and then to "Test run".
 */
export function runTitle(run: Pick<Run, 'testNames' | 'results'>): string {
  const names = run.testNames?.length ? run.testNames : (run.results ?? []).flatMap((r) => (r.testCase?.name ? [r.testCase.name] : []));
  if (names.length === 0) return 'Test run';
  if (names.length <= 2) return names.join(', ');
  return `${names[0]} + ${names.length - 1} more`;
}

/** Text a run can be found by: project, every test name and both id forms. */
export function runSearchText(run: Run): string {
  return [projectNameOf(run), ...(run.testNames ?? []), run.id].join(' ').toLowerCase();
}

/**
 * Copies text. `navigator.clipboard` only exists on secure pages (https or localhost), so 1QA opened over
 * plain http on a server address falls back to a hidden textarea + `execCommand('copy')`. The textarea goes
 * inside the open modal <dialog>, if any, because a modal makes everything outside it inert.
 */
export async function copyText(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    /* fall through to the fallback */
  }
  const host = document.activeElement?.closest('dialog') ?? document.querySelector('dialog[open]') ?? document.body;
  const area = document.createElement('textarea');
  area.value = text;
  area.setAttribute('readonly', '');
  area.style.position = 'fixed';
  area.style.top = '0';
  area.style.left = '0';
  area.style.opacity = '0';
  host.appendChild(area);
  const previous = document.activeElement as HTMLElement | null;
  area.select();
  let ok = false;
  try {
    ok = document.execCommand('copy');
  } catch {
    ok = false;
  }
  area.remove();
  previous?.focus?.();
  return ok;
}

/** Same rule as the API: the e2e engine only accepts https, except plain http on localhost. */
export const URL_RULE_MESSAGE = 'Use an https:// address (plain http only works for localhost)';
export const URL_HELP = 'Use the https:// address of the site you want to test, e.g. https://staging.myshop.com';

export function isValidUrl(value: string): boolean {
  let u: URL;
  try {
    u = new URL(value);
  } catch {
    return false;
  }
  if (!u.hostname) return false;
  if (u.protocol === 'https:') return true;
  if (u.protocol !== 'http:') return false;
  const host = u.hostname.toLowerCase();
  return host === 'localhost' || host === '127.0.0.1' || host.endsWith('.localhost');
}
